// ─────────────────────────────────────────────────────────────────────────────
// Minimum password length, as set in Settings → Auth (password_min_length).
//
// Read from GET /api/v1/auth/password-policy, which needs no session, so the
// setup, change-password and reset-password pages can use it. Until it loads,
// or if it cannot be read, the pages use 10 (the factory default); the C++
// service enforces the real minimum either way and its message is shown.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';

const DEFAULT_MIN_LENGTH = 10;

/** Read once per page load and shared by every page that asks. */
let cached: number | null = null;

export function usePasswordMinLength(): number {
  const [minLength, setMinLength] = useState<number>(cached ?? DEFAULT_MIN_LENGTH);

  useEffect(() => {
    if (cached !== null) return;
    let cancelled = false;
    fetch('/api/v1/auth/password-policy', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { min_length?: unknown } | null) => {
        const n = Number(data?.min_length);
        if (!Number.isInteger(n) || n <= 0) return;
        cached = n;
        if (!cancelled) setMinLength(n);
      })
      .catch(() => { /* keep the default; C++ still enforces */ });
    return () => { cancelled = true; };
  }, []);

  return minLength;
}
